// ProfileCardList.tsx
import { useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Star,
  Heart,
  Share2,
  Eye,
  ChevronRight,
  Calendar,
  Clock,
  User,
  Users,
  Briefcase,
  HeartHandshake,
  GraduationCap,
  Activity,
  Coffee,
  Music,
  Camera,
  BookOpen,
  Dumbbell,
  Globe,
  MessageCircle,
  Shield,
  Zap,
  Award,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatRelativeTime } from "../helpers/TimeFormats";
import myIcons from "@/assets/Icons";

// Profile interface matching your schema
interface Profile {
  user_id: string;
  nickname: string;
  age: number;
  gender: string;
  city: string;
  country: string;
  origin_state?: string;
  bio: string;
  interests: string[];
  height_cm: number;
  body_build: string;
  complexion: string;
  hair_color: string;
  eye_color: string;
  ethnicity: string;
  tribe: string;
  seeking_status: string;
  last_active_at: number;
  profile_views: number;
  created_at: number;
  updated_at: number;
  preferences: {
    preferred_age_min: number;
    preferred_age_max: number;
    relocation: string;
  };
  desired_qualities: {
    qualities: string[];
  };
  photos: string[];
  is_active: boolean;
  marital_status?: string;
  education?: string;
  occupation?: string;
  religion?: string;
  likes_count?: number;
  saves_count?: number;
  super_likes_count?: number;
  my_reaction_type?: string;
  verified?: boolean;
  premium?: boolean;
}

interface ProfileCardListProps {
  profile: Profile;
  onViewDetails?: (profile: Profile) => void;
  onLike?: (profile: Profile) => void;
  onSuperLike?: (profile: Profile) => void;
  onSave?: (profile: Profile) => void;
  onMessage?: (profile: Profile) => void;
}

// Interest icons mapping
const interestIcons: Record<string, any> = {
  "Music": Music,
  "Travel": Globe,
  "Sports": Activity,
  "Reading": BookOpen,
  "Gym": Dumbbell,
  "Coffee": Coffee,
  "Photography": Camera,
  "Art": Camera,
  "Dancing": Activity,
  "Cooking": Coffee,
  "Movies": Camera,
  "Gaming": Zap,
  "Yoga": Activity,
  "Hiking": Activity,
  "Swimming": Activity,
  "Volunteering": HeartHandshake,
  "Teaching": GraduationCap,
  "Business": Briefcase,
  "Tech": Zap,
  "Fashion": User,
};

export default function ProfileCardList({ 
  profile, 
  onViewDetails, 
  onLike, 
  onSuperLike,
  onSave,
  onMessage 
}: ProfileCardListProps) {
  const [isLiked, setIsLiked] = useState(profile?.my_reaction_type === 'like');
  const [isSaved, setIsSaved] = useState(profile?.my_reaction_type === 'save');
  const [isSuperLiked, setIsSuperLiked] = useState(profile?.my_reaction_type === 'super_like');
  const [isHovered, setIsHovered] = useState(false);

  const isMale = profile?.gender === "m" || profile?.gender === "male";
  const displayName = profile?.nickname || "Anonymous";
  const locationDisplay = profile?.city 
    ? (profile?.country ? `${profile?.city}, ${profile?.country}` : profile?.city)
    : (profile?.origin_state || "Location not specified");

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    onLike?.(profile);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSaved(!isSaved);
    onSave?.(profile);
  };

  const handleSuperLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSuperLiked(!isSuperLiked);
    onSuperLike?.(profile);
  };

  const handleMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMessage?.(profile);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/profile/${profile?.user_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${displayName}, ${profile?.age}`,
          text: `Check out ${displayName}'s profile`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Profile link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  const handleClick = () => {
    onViewDetails?.(profile);
  };

  const getSeekingStatusColor = () => {
    switch (profile?.seeking_status) {
      case "seeking":
        return "bg-green-500";
      case "not_seeking":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getSeekingStatusText = () => {
    switch (profile?.seeking_status) {
      case "seeking":
        return "Actively Seeking";
      case "not_seeking":
        return "Not Seeking";
      default:
        return profile?.seeking_status || "Unknown";
    }
  };

  const getCompatibilityScore = () => {
    // This is a mock calculation - replace with actual compatibility algorithm
    const preferences = profile?.preferences;
    const desiredQualities = profile?.desired_qualities?.qualities || [];
    let score = 70; // Base score
    
    if (preferences?.preferred_age_min && profile?.age) {
      if (profile.age >= preferences.preferred_age_min && 
          profile.age <= (preferences.preferred_age_max || 100)) {
        score += 10;
      }
    }
    
    if (desiredQualities.length > 0) {
      score += Math.min(desiredQualities.length * 2, 20);
    }
    
    return Math.min(score, 98);
  };

  const formatLastActive = () => {
    if (!profile?.last_active_at) return "Never";
    return formatRelativeTime(new Date(profile.last_active_at * 1000).toISOString());
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleClick}
      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg dark:hover:shadow-gray-900/30 transition-all cursor-pointer"
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Left Section - Avatar Thumbnail */}
          <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0">
            <div className={`w-full h-full rounded-lg overflow-hidden bg-gradient-to-br ${
              isMale 
                ? "from-indigo-100 to-indigo-200 dark:from-indigo-900/30 dark:to-indigo-800/30" 
                : "from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30"
            }`}>
              {/* Badges */}
              <div className="absolute top-2 left-2 flex gap-1 z-10">
                {profile?.verified && (
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full flex items-center gap-0.5">
                    <CheckCircle className="w-3 h-3" />
                    Verified
                  </span>
                )}
                {profile?.premium && (
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-yellow-400 to-yellow-600 text-white rounded-full">
                    Premium
                  </span>
                )}
              </div>

              {/* Online Status Dot */}
              {profile?.is_active && (
                <div className="absolute top-2 right-2 z-10">
                  <div className="w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white dark:ring-gray-800"></div>
                </div>
              )}

              {/* Avatar Image */}
              {profile?.photos && profile.photos.length > 0 ? (
                <img 
                  src={profile.photos[0]} 
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {isMale ? (
                         <img src={myIcons.man} className='h-full object-cover' />
                       ) : (
                         <img src={myIcons.woman} className='h-full object-cover'/>
                       )}
                </div>
              )}

              {/* Age Badge */}
              <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 dark:bg-gray-900/80 backdrop-blur-sm rounded-full text-white text-xs font-semibold">
                {profile?.age}
              </div>
            </div>
          </div>
          
          {/* Middle Section - Content */}
          <div className="flex-1 min-w-0">
            {/* Header Info */}
            <div className="flex flex-wrap items-center gap-2 mb-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Member since {new Date(profile?.created_at * 1000).getFullYear()}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Active {formatLastActive()}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate max-w-[150px] md:max-w-[200px]">{locationDisplay}</span>
              </span>
            </div>
            
            {/* Name and Basic Info */}
            <div className="flex flex-wrap items-baseline gap-2 mb-2">
              <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                {displayName}
              </h3>
              {profile?.marital_status && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  • {profile.marital_status}
                </span>
              )}
              {profile?.ethnicity && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  • {profile.ethnicity}
                </span>
              )}
            </div>
            
            {/* Stats and Status */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              {/* Seeking Status */}
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${getSeekingStatusColor()}`} />
                <span className={`text-xs font-medium ${
                  profile?.seeking_status === "seeking" 
                    ? "text-green-600 dark:text-green-400" 
                    : "text-red-600 dark:text-red-400"
                }`}>
                  {getSeekingStatusText()}
                </span>
              </div>

              {/* Compatibility Score */}
              <div className="flex items-center gap-1">
                <Award className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                  {getCompatibilityScore()}% Match
                </span>
              </div>

              {/* Profile Views */}
              {profile?.profile_views > 0 && (
                <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Eye className="w-3 h-3" />
                  {profile.profile_views} views
                </span>
              )}
            </div>

            {/* Bio Preview */}
            {profile?.bio && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                {profile.bio}
              </p>
            )}
            
            {/* Interests */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {profile?.interests?.slice(0, 4).map((interest) => {
                const Icon = interestIcons[interest] || HeartHandshake;
                return (
                  <span key={interest} className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                    <Icon className="w-3 h-3" />
                    {interest}
                  </span>
                );
              })}
              {profile?.interests?.length > 4 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                  +{profile.interests.length - 4} more
                </span>
              )}
            </div>

            {/* Physical Attributes */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
              {profile?.height_cm > 0 && (
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  {profile.height_cm} cm
                </span>
              )}
              {profile?.body_build && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {profile.body_build}
                </span>
              )}
              {profile?.complexion && (
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  {profile.complexion} skin
                </span>
              )}
              {profile?.hair_color && profile?.eye_color && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {profile.hair_color} hair, {profile.eye_color} eyes
                </span>
              )}
            </div>

            {/* Preferences Preview */}
            {profile?.preferences && (
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                {profile.preferences.preferred_age_min && profile.preferences.preferred_age_max && (
                  <span className="text-gray-500 dark:text-gray-400">
                    Seeking age: {profile.preferences.preferred_age_min}-{profile.preferences.preferred_age_max}
                  </span>
                )}
                {profile.preferences.relocation && (
                  <span className="text-gray-500 dark:text-gray-400">
                    • Relocation: {profile.preferences.relocation === 'Yes' ? 'Yes' : 'No'}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Right Section - Actions */}
          <div className="flex flex-col gap-2">
            {/* Like Button */}
            <button
              onClick={handleLike}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group/btn"
              title={isLiked ? "Unlike" : "Like"}
            >
              <Heart 
                className={`w-5 h-5 transition-all ${
                  isLiked 
                    ? "fill-red-500 text-red-500 scale-110" 
                    : "text-gray-400 group-hover/btn:text-red-500 group-hover/btn:scale-110"
                }`} 
              />
            </button>

            {/* Super Like Button */}
            <button
              onClick={handleSuperLike}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group/btn"
              title={isSuperLiked ? "Remove Super Like" : "Super Like"}
            >
              <Zap 
                className={`w-5 h-5 transition-all ${
                  isSuperLiked 
                    ? "fill-yellow-500 text-yellow-500 scale-110" 
                    : "text-gray-400 group-hover/btn:text-yellow-500 group-hover/btn:scale-110"
                }`} 
              />
            </button>

            {/* Save Button */}
            <button
              onClick={handleSave}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group/btn"
              title={isSaved ? "Unsave" : "Save"}
            >
              <BookOpen 
                className={`w-5 h-5 transition-all ${
                  isSaved 
                    ? "fill-blue-500 text-blue-500 scale-110" 
                    : "text-gray-400 group-hover/btn:text-blue-500 group-hover/btn:scale-110"
                }`} 
              />
            </button>

            {/* Message Button */}
            <button
              onClick={handleMessage}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group/btn"
              title="Send Message"
            >
              <MessageCircle className="w-5 h-5 text-gray-400 group-hover/btn:text-purple-500 transition-colors" />
            </button>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group/btn"
              title="Share Profile"
            >
              <Share2 className="w-5 h-5 text-gray-400 group-hover/btn:text-purple-500 transition-colors" />
            </button>

            {/* Mobile Details Button */}
            <button
              onClick={handleClick}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors md:hidden"
              title="View details"
            >
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Hover indicator for desktop */}
        <div className={`hidden md:block mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 transition-all duration-200 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}>
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              {profile?.desired_qualities?.qualities?.slice(0, 2).map((quality, idx) => (
                <span key={idx} className="text-xs text-gray-500 dark:text-gray-400">
                  Seeking: {quality}
                </span>
              ))}
            </div>
            <button className="text-sm text-purple-500 hover:text-purple-600 font-medium flex items-center gap-1">
              View full profile
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap gap-4 mt-3 pt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
          {profile?.likes_count !== undefined && profile.likes_count > 0 && (
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" />
              {profile.likes_count} likes
            </span>
          )}
          {profile?.saves_count !== undefined && profile.saves_count > 0 && (
            <span className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              {profile.saves_count} saves
            </span>
          )}
          {profile?.super_likes_count !== undefined && profile.super_likes_count > 0 && (
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {profile.super_likes_count} super likes
            </span>
          )}
          {profile?.tribe && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              Tribe: {profile.tribe}
            </span>
          )}
          {profile?.religion && (
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              {profile.religion}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Optional: Export a wrapper component for multiple profiles
interface ProfileCardListWrapperProps {
  profiles: Profile[];
  onViewDetails?: (profile: Profile) => void;
  onLike?: (profile: Profile) => void;
  onSuperLike?: (profile: Profile) => void;
  onSave?: (profile: Profile) => void;
  onMessage?: (profile: Profile) => void;
  emptyMessage?: string;
  className?: string;
}

export function ProfileCardListWrapper({
  profiles,
  onViewDetails,
  onLike,
  onSuperLike,
  onSave,
  onMessage,
  emptyMessage = "No profiles found",
  className = ""
}: ProfileCardListWrapperProps) {
  if (!profiles || profiles.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {profiles.map((profile) => (
        <ProfileCardList
          key={profile.user_id}
          profile={profile}
          onViewDetails={onViewDetails}
          onLike={onLike}
          onSuperLike={onSuperLike}
          onSave={onSave}
          onMessage={onMessage}
        />
      ))}
    </div>
  );
}